import queryString from "query-string";
import AxiosInstance from "@/config/AxiosInstance";
import { API_BASE_URL } from "@/config/appConfig";
import { BRAND_REQUEST_TYPE } from "@/config/constants";

type RequestId = string | number | undefined;
type QueryParams = Record<string, string | number | boolean | null | undefined>;
type AxiosLikeResponse<TData = unknown> = {
  data: TData;
};

type BrandLocationParams = {
  id?: RequestId;
  groupId?: string | number;
};

type BrandIdParams = {
  fk_brand_id?: RequestId;
};

type CampaignListingParams = {
  id?: RequestId;
  userId?: RequestId;
};

type BudgetTemplateParams = QueryParams | null | undefined;

export const requestService = {
  getChannelAndRequests,
  getChannel,
  getBrandLocations,
  getGroupsBrandRequest,
  getCampaignListing,
  getBrandRequestListing,
  getBrandRequestGetById,
  brandRequestCreate,
  checkCampaignNameExists,
  downloadBudgetTemplateService,
  getCampaignGoals,
};

function getChannelAndRequests() {
  return AxiosInstance.get(`${API_BASE_URL}/channel-and-requests`);
}

function getChannel() {
  return AxiosInstance.get(`${API_BASE_URL}/channels`);
}

function getBrandLocations(data: BrandLocationParams = {}) {
  const { groupId, id } = data;
  const query = groupId ? `?id=${id}&group_ids=${groupId}` : `?id=${id}`;
  return AxiosInstance.get(`${API_BASE_URL}/getBrandLocations${query}`);
}

function getGroupsBrandRequest(data: BrandIdParams) {
  const { fk_brand_id } = data;
  return AxiosInstance.get(
    `${API_BASE_URL}/groups-brand-request?fk_brand_id=${fk_brand_id}`
  );
}

function getCampaignListing(data: CampaignListingParams) {
  const { id, userId } = data;
  return AxiosInstance.get(
    `${API_BASE_URL}/campaignListing?id=${id}&userId=${userId}`
  );
}

function getBrandRequestListing<TData = unknown>(
  data: QueryParams,
  isSWR?: boolean
) {
  const q = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL}/brandRequestListing${q ? "?" + q : ""}`
    ).then((res: AxiosLikeResponse<TData>) => res.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL}/brandRequestListing${q ? "?" + q : ""}`
  );
}

function getBrandRequestGetById<TData = unknown>(id: string, isSWR?: boolean) {
  if (isSWR) {
    return AxiosInstance.get(`${API_BASE_URL}/brandRequestGetById/${id}`).then(
      (res: AxiosLikeResponse<TData>) => res.data
    );
  }
  return AxiosInstance.get(`${API_BASE_URL}/brandRequestGetById/${id}`);
}

function brandRequestCreate(data: FormData | Record<string, unknown>) {
  return AxiosInstance.post(`${API_BASE_URL}/brandRequestCreate`, data);
}

function checkCampaignNameExists(data: QueryParams) {
  const query = queryString.stringify(data);
  return AxiosInstance.get(`${API_BASE_URL}/getBrandCampaigns?${query}`);
}

function downloadBudgetTemplateService(
  type: string,
  params?: BudgetTemplateParams,
  id?: RequestId,
  date_range_start_date?: string | null,
  date_range_end_date?: string | null
) {
  const endpoints: Record<string, string> = {
    [BRAND_REQUEST_TYPE.BUDGET_APPROVAL_CONFIRMATION_REQUEST]:
      "/download-template-budget-location",
  };

  const endpoint = endpoints[type];
  if (!endpoint) throw new Error("Invalid template type");

  const queryParams = {
    id,
    ...(params || {}),
    ...(date_range_start_date && { date_range_start_date }),
    ...(date_range_end_date && { date_range_end_date }),
  };

  return AxiosInstance.get(`${API_BASE_URL}${endpoint}`, {
    params: queryParams,
  });
}

function getCampaignGoals() {
  return AxiosInstance.get(`${API_BASE_URL}/brandRequest-campaign-goal`);
}
